import { useCallback, useEffect, useRef, useState } from 'react';
import type { PdvDestaqueItem, PdvEngajamentoBroadcast, PdvSnapshotEngajamento } from '@/shared/types';
import { pdvEngajamentoRepository } from '../../data/pdvEngajamentoRepository';

const TICK_MS           = 1_000;
const REFRESH_MS        = 60_000;
const DURACAO_DESTAQUE  = 8_000;
const DURACAO_MARCO     = 6_000;
const MARCO_VALIDADE_MS = 10 * 60_000;

/** Gatilho no ar: card de destaque (canto superior direito) ou termômetro transbordando. */
export type OverlayPdv =
  | { tipo: 'destaque';   itens: PdvDestaqueItem[] }
  | { tipo: 'hype_marco'; marco: number; total: number; hype_rotulo: string };

interface Params {
  telaId:     string;
  /** modoPlaylistJukebox && exibir_pdv_engajamento */
  habilitado: boolean;
  /** Algo de prioridade maior está no ar (popup de pedido, Momento Especial, gol). */
  bloqueado:  boolean;
}

const SNAPSHOT_VAZIO: PdvSnapshotEngajamento = {
  ativo: false,
  contadores: { hype_hoje: 0, hype_ultima_hora: 0, proximo_marco: null, hype_rotulo: 'chopps' },
};

/**
 * Consome o evento `pdv_engajamento` (via callback do usePlayerSockets — não abre
 * canal próprio) e decide quando cada gatilho vai ao ar. Fila com no máximo 1
 * pendente por tipo; o destaque sai antes do marco e o outro sobrevive.
 */
export function usePdvEngajamento({ telaId, habilitado, bloqueado }: Params) {
  const [snapshot, setSnapshot] = useState<PdvSnapshotEngajamento>(SNAPSHOT_VAZIO);
  const [overlay, setOverlay]   = useState<OverlayPdv | null>(null);

  const destaquePendenteRef = useRef<PdvDestaqueItem[] | null>(null);
  const hypePendenteRef     = useRef<{ payload: Extract<OverlayPdv, { tipo: 'hype_marco' }>; recebidoEm: number } | null>(null);
  const overlayRef          = useRef<OverlayPdv | null>(null);
  const overlayTimerRef     = useRef<ReturnType<typeof setTimeout> | null>(null);
  const habilitadoRef       = useRef(habilitado);
  const bloqueadoRef        = useRef(bloqueado);

  useEffect(() => { habilitadoRef.current = habilitado; }, [habilitado]);
  useEffect(() => { bloqueadoRef.current = bloqueado; }, [bloqueado]);
  useEffect(() => { overlayRef.current = overlay; }, [overlay]);

  const carregarSnapshot = useCallback(() => {
    if (!habilitadoRef.current) return;
    void pdvEngajamentoRepository.snapshot(telaId)
      .then(setSnapshot)
      .catch(() => setSnapshot(SNAPSHOT_VAZIO));
  }, [telaId]);

  // `habilitado` é false no boot da TV (tela ainda não carregou): recarrega quando
  // liga, zera quando desliga e renova a cada 60 s (vira o dia BRT sem evento novo).
  useEffect(() => {
    if (!habilitado) { setSnapshot(SNAPSHOT_VAZIO); return; }
    carregarSnapshot();
    const t = setInterval(carregarSnapshot, REFRESH_MS);
    return () => clearInterval(t);
  }, [habilitado, carregarSnapshot]);

  // Dreno da fila.
  useEffect(() => {
    const interval = setInterval(() => {
      if (!habilitadoRef.current || bloqueadoRef.current || overlayRef.current) return;
      // Marco que ficou preso (bloqueio longo) não vai ao ar com número velho.
      if (hypePendenteRef.current && Date.now() - hypePendenteRef.current.recebidoEm > MARCO_VALIDADE_MS) {
        hypePendenteRef.current = null;
      }
      const proximo = destaquePendenteRef.current
        ? { tipo: 'destaque' as const, itens: destaquePendenteRef.current }
        : hypePendenteRef.current?.payload ?? null;
      if (!proximo) return;

      // Só consome o que vai ao ar — um marco pendente sobrevive ao destaque.
      if (proximo.tipo === 'destaque') destaquePendenteRef.current = null;
      else hypePendenteRef.current = null;
      setOverlay(proximo);
      if (overlayTimerRef.current) clearTimeout(overlayTimerRef.current);
      overlayTimerRef.current = setTimeout(
        () => setOverlay(atual => atual === proximo ? null : atual),
        proximo.tipo === 'destaque' ? DURACAO_DESTAQUE : DURACAO_MARCO,
      );
    }, TICK_MS);
    return () => {
      clearInterval(interval);
      if (overlayTimerRef.current) clearTimeout(overlayTimerRef.current);
    };
  }, []);

  const receber = useCallback((payload: PdvEngajamentoBroadcast) => {
    if (payload.tipo === 'atualizar') { carregarSnapshot(); return; }
    if (payload.tipo === 'destaque')   destaquePendenteRef.current = payload.itens;
    if (payload.tipo === 'hype_marco') hypePendenteRef.current = { payload, recebidoEm: Date.now() };
    carregarSnapshot();
  }, [carregarSnapshot]);

  const testarDestaque = useCallback(() => {
    receber({ tipo: 'destaque', itens: [{ nome_exibicao: 'Prato de destaque', frase: 'Saiu um prato de destaque!', imagem_url: null, video_url: null, quantidade: 1 }] });
  }, [receber]);

  return { snapshot, overlay, receber, testarDestaque };
}
